import type { ReactElement } from 'react';
import { Link, Navigate, Route, Routes } from 'react-router-dom';
import { useI18n } from '../lib/i18n/I18nProvider';
import { Portal } from './Portal';
import type { NavSection } from './Shell';
import { BrandLogo } from '../ui/BrandLogo';

import { AdminExpos } from '../portals/admin/Expos';
import { AdminExpoLayout, AdminExpoLifecycle } from '../portals/admin/ExpoDetail';
import { AdminFloor, AdminLayouts, AdminParticipations } from '../portals/admin/Floor';
import { AdminReleases } from '../portals/admin/Releases';
import { AdminSponsors } from '../portals/admin/Sponsors';
import { AdminOrganizations } from '../portals/admin/People';
import { AdminApprovals } from '../portals/admin/Approvals';
import { AdminCustomers, AdminDirectLeads, AdminExceptions, AdminIdentities } from '../portals/admin/Crm';

import {
  BrokerageExceptions,
  BrokerageInvitations,
  BrokerageLeads,
  BrokerageLinks,
  BrokeragePendingReassignment,
} from '../portals/brokerage/Brokerage';

import {
  DeveloperBooth,
  DeveloperCatalogue,
  DeveloperCompany,
  DeveloperFloor,
  DeveloperHolds,
} from '../portals/developer/Developer';

import {
  CustomerAttribution,
  CustomerConsents,
  CustomerEnrolments,
  CustomerShortlist,
} from '../portals/customer/Customer';

function PortalPicker(): ReactElement {
  const { t, toggleLocale, dir } = useI18n();
  return (
    <div className="auth-page" dir={dir}>
      <div className="auth-card" style={{ width: 'min(56rem, 100%)' }}>
        <div className="auth-card__brand">
          <BrandLogo alt={t('brand.name')} />
          <p className="muted">{t('brand.tagline')}</p>
        </div>
        <h1>{t('portal.choose')}</h1>
        <p className="muted">{t('portal.choose.hint')}</p>
        <div className="portal-picker">
          <Link to="/admin">
            <strong>{t('portal.admin')}</strong>
            <span className="muted">{t('portal.admin.desc')}</span>
          </Link>
          <Link to="/brokerage">
            <strong>{t('portal.brokerage')}</strong>
            <span className="muted">{t('portal.brokerage.desc')}</span>
          </Link>
          <Link to="/developer">
            <strong>{t('portal.developer')}</strong>
            <span className="muted">{t('portal.developer.desc')}</span>
          </Link>
          <Link to="/portal">
            <strong>{t('portal.customer')}</strong>
            <span className="muted">{t('portal.customer.desc')}</span>
          </Link>
        </div>
        <button type="button" className="btn btn--secondary btn--small" onClick={toggleLocale}>
          {t('lang.toggle')}
        </button>
      </div>
    </div>
  );
}

function useAdminSections(): NavSection[] {
  const { t } = useI18n();
  return [
    {
      title: t('portal.admin'),
      links: [
        { to: '/admin', label: t('admin.expos.title'), end: true },
        { to: '/admin/layouts', label: t('admin.layouts.title') },
        { to: '/admin/organizations', label: t('admin.orgs.title') },
      ],
    },
    {
      title: t('admin.customers.title'),
      links: [
        { to: '/admin/customers', label: t('admin.customers.title') },
        { to: '/admin/direct-leads', label: t('admin.leads.title') },
        { to: '/admin/exceptions', label: t('admin.exceptions.title') },
        { to: '/admin/identities', label: t('admin.identity.title') },
        { to: '/admin/approvals', label: t('admin.approvals.title') },
      ],
    },
  ];
}

function useBrokerageSections(): NavSection[] {
  const { t } = useI18n();
  return [
    {
      title: t('portal.brokerage'),
      links: [
        { to: '/brokerage', label: t('brk.leads.title'), end: true },
        { to: '/brokerage/pending-reassignment', label: t('brk.pending.title') },
        { to: '/brokerage/invitations', label: t('brk.invitations.title') },
        { to: '/brokerage/links', label: t('brk.links.title') },
        { to: '/brokerage/exceptions', label: t('admin.exceptions.title') },
      ],
    },
  ];
}

function useDeveloperSections(): NavSection[] {
  const { t } = useI18n();
  return [
    {
      title: t('dev.duty.title'),
      links: [{ to: '/developer', label: t('dev.duty.title'), end: true }],
    },
    {
      title: t('portal.developer'),
      links: [
        { to: '/developer/company', label: t('dev.company.title') },
        { to: '/developer/booth', label: t('dev.booth.title') },
        { to: '/developer/catalogue', label: t('dev.projects.title') },
        { to: '/developer/holds', label: t('dev.holds.title') },
      ],
    },
  ];
}

function useCustomerSections(): NavSection[] {
  const { t } = useI18n();
  return [
    {
      title: t('portal.customer'),
      links: [
        { to: '/portal', label: t('cus.enrollments.title'), end: true },
        { to: '/portal/shortlist', label: t('cus.shortlist.title') },
        { to: '/portal/privacy', label: t('cus.consents.title') },
        { to: '/portal/attribution', label: t('cus.attribution.title') },
      ],
    },
  ];
}

export function App(): ReactElement {
  const adminSections = useAdminSections();
  const brokerageSections = useBrokerageSections();
  const developerSections = useDeveloperSections();
  const customerSections = useCustomerSections();
  const { t } = useI18n();

  return (
    <Routes>
      <Route path="/" element={<PortalPicker />} />

      <Route
        path="/admin"
        element={<Portal surface="dashboard" portalName={t('portal.admin')} sections={adminSections} />}
      >
        <Route index element={<AdminExpos />} />
        <Route path="layouts" element={<AdminLayouts />} />
        <Route path="organizations" element={<AdminOrganizations />} />
        <Route path="customers" element={<AdminCustomers />} />
        <Route path="direct-leads" element={<AdminDirectLeads />} />
        <Route path="exceptions" element={<AdminExceptions />} />
        <Route path="identities" element={<AdminIdentities />} />
        <Route path="approvals" element={<AdminApprovals />} />
        <Route path="expos/:expoId" element={<AdminExpoLayout />}>
          <Route index element={<AdminExpoLifecycle />} />
          <Route path="floor" element={<AdminFloor />} />
          <Route path="participations" element={<AdminParticipations />} />
          <Route path="releases" element={<AdminReleases />} />
          <Route path="sponsors" element={<AdminSponsors />} />
        </Route>
      </Route>

      <Route
        path="/brokerage"
        element={<Portal surface="dashboard" portalName={t('portal.brokerage')} sections={brokerageSections} />}
      >
        <Route index element={<BrokerageLeads />} />
        <Route path="pending-reassignment" element={<BrokeragePendingReassignment />} />
        <Route path="invitations" element={<BrokerageInvitations />} />
        <Route path="links" element={<BrokerageLinks />} />
        <Route path="exceptions" element={<BrokerageExceptions />} />
      </Route>

      <Route
        path="/developer"
        element={<Portal surface="dashboard" portalName={t('portal.developer')} sections={developerSections} />}
      >
        <Route index element={<DeveloperFloor />} />
        <Route path="company" element={<DeveloperCompany />} />
        <Route path="booth" element={<DeveloperBooth />} />
        <Route path="catalogue" element={<DeveloperCatalogue />} />
        <Route path="holds" element={<DeveloperHolds />} />
      </Route>

      <Route
        path="/portal"
        element={<Portal surface="portal" portalName={t('portal.customer')} sections={customerSections} />}
      >
        <Route index element={<CustomerEnrolments />} />
        <Route path="shortlist" element={<CustomerShortlist />} />
        <Route path="privacy" element={<CustomerConsents />} />
        <Route path="attribution" element={<CustomerAttribution />} />
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
